import { IconButton, InputAdornment, TextField } from "@mui/material";
import React from "react";
import ChevronDownIcon from "../../../../../components/icons/ChevronDownIcon";
import { styled, useTheme } from "@mui/material/styles";

type BookingQuantityInputProps = {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
};

const clamp = (value: number, min?: number, max?: number) => {
  let next = value;
  if (typeof min === "number") {
    next = Math.max(min, next);
  }
  if (typeof max === "number" && max > 0) {
    next = Math.min(max, next);
  }
  return next;
};

const BookingQuantityInput: React.FC<BookingQuantityInputProps> = ({
  label,
  value,
  onChange,
  min = 1,
  max,
  step = 1,
  disabled = false,
}) => {
  const DISABLED_COLOR = "#bdbdbd";
  const theme = useTheme();

  const StyledTextField = styled(TextField, {
    shouldForwardProp: (prop) => prop !== "$value" && prop !== "$disabledProp",
  })<{
    $value: number | null;
    $disabledProp: boolean;
  }>(({ theme, $value, $disabledProp }) => ({
    backgroundColor: theme.palette.background.default,
    "& .MuiOutlinedInput-root": {
      height: "62px",
      "& .MuiInputBase-input": {
        color: $disabledProp ? DISABLED_COLOR : theme.palette.primary.main,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: $disabledProp
          ? DISABLED_COLOR
          : $value && $value !== 0
          ? theme.palette.primary.light
          : theme.palette.primary.contrastText,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: $disabledProp ? DISABLED_COLOR : theme.palette.primary.light,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: $disabledProp ? DISABLED_COLOR : theme.palette.primary.light,
      },
    },
    "& .MuiInputLabel-root": {
      color: $disabledProp ? DISABLED_COLOR : "#333333",
    },
    "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: DISABLED_COLOR,
    },
  }));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      onChange(min);
      return;
    }
    onChange(clamp(parsed, min, max));
  };

  const handleIncrement = () => {
    const next = clamp((value ?? 0) + step, min, max);
    onChange(next);
  };

  const handleDecrement = () => {
    const next = clamp((value ?? 0) - step, min, max);
    onChange(next);
  };

  const arrowColor = disabled
    ? DISABLED_COLOR
    : value && value !== 0
    ? theme.palette.primary.light
    : theme.palette.primary.contrastText;

  return (
    <StyledTextField
      type="number"
      label={label}
      value={value}
      onChange={handleChange}
      inputProps={{ min, max: max && max > 0 ? max : undefined, step }}
      fullWidth
      disabled={disabled}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <div style={{ display: "flex", flexDirection: "column" }}>
              <IconButton
                size="small"
                onClick={handleIncrement}
                disabled={disabled || (typeof max === "number" && max > 0 && value >= max)}
                aria-label="increase"
              >
                <ChevronDownIcon style={{ transform: "rotate(180deg)", width: 18, height: 18, color: arrowColor }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleDecrement}
                disabled={disabled || value <= (min ?? 1)}
                aria-label="decrease"
              >
                <ChevronDownIcon style={{ width: 18, height: 18, color: arrowColor }} />
              </IconButton>
            </div>
          </InputAdornment>
        ),
      }}
      $value={value}
      $disabledProp={disabled}
    />
  );
};

export default BookingQuantityInput;


